import React, { useEffect, useMemo, useRef, useState } from "react";
import { ActionIcon, Badge, Group, SegmentedControl, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { Button, spacePx } from "@xplatform/design-system";
import { IconArrowBackUp, IconCheck, IconEdit, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { DataTableColumn } from "@xplatform/design-system";
import type { CollectionOp, JSONSchema } from "@models/mappingConfig";
import { AdminTable, col, COLUMN_WIDTH } from "@components/AdminTable";
import { collectionOps, deepEqual, describeItemFields, keyString, type FieldDescriptor } from "@utils/mappingConfig";
import { ValueInput } from "./ValueInput";
import { formatValue } from "./format";

type Item = Record<string, unknown>;

export interface CollectionTableProps {
  /** The collection field (kind `collection`, with `x-key`). */
  descriptor: FieldDescriptor;
  /** Root JSON Schema (for `$defs`). */
  root: JSONSchema;
  value: Item[];
  /** Baseline the `$ops` are computed against; defaults to the first `value` seen. */
  original?: Item[];
  /** Every edit: the new items and the `$ops` turning `original` into them. */
  onChange?: (items: Item[], ops: CollectionOp[]) => void;
  readOnly?: boolean;
  /** Server field errors per row key (`path` `field[key=K].sub`). */
  rowErrors?: Record<string, string[]>;
  /** Reports a client-side problem that must block saving (empty or duplicate key). */
  onInvalid?: (message: string | null) => void;
  pageSize?: number;
}

type Filter = "all" | "changed" | "errors";

const emptyValueFor = (field: FieldDescriptor): unknown => {
  if ("default" in field.schema) return field.schema.default;
  switch (field.kind) {
    case "enumArray":
    case "stringArray":
    case "collection":
      return [];
    case "boolean":
      return false;
    case "integer":
    case "number":
      return null;
    case "string":
    case "prompt":
      return field.nullable ? null : "";
    default:
      return field.nullable ? null : undefined;
  }
};

/**
 * A list-of-objects config field (`x-key`) as an editable table on AdminTable.
 * Columns come from the item model in `$defs`; rows are matched to the baseline
 * by their key, so every edit maps to `$ops` (add / update with only the
 * changed fields / remove). The key of an existing row is read-only: changing
 * it is a remove plus an add.
 */
export const CollectionTable: React.FC<CollectionTableProps> = ({
  descriptor, root, value, original, onChange, readOnly, rowErrors = {}, onInvalid, pageSize = 20,
}) => {
  const { t } = useTranslation();
  const key = descriptor.collectionKey ?? "id";
  const itemFields = useMemo(() => describeItemFields(descriptor.itemSchema, root), [descriptor.itemSchema, root]);
  const keyField = itemFields.find((f) => f.name === key);
  const baselineRef = useRef<Item[]>(original ?? value);
  const baseline = original ?? baselineRef.current;
  const baselineByKey = useMemo(() => new Map(baseline.map((item) => [keyString(item[key]), item])), [baseline, key]);

  // Stable row ids across edits (an edited row is a new object; a new row's key changes as it is typed).
  const ids = useRef(new WeakMap<Item, string>());
  const counter = useRef(0);
  const rowId = (item: Item): string => {
    let id = ids.current.get(item);
    if (!id) {
      id = `row-${counter.current++}`;
      ids.current.set(item, id);
    }
    return id;
  };

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<string | null>(null);

  const keyProblems = useMemo(() => {
    const seen = new Map<string, number>();
    for (const item of value) {
      const k = keyString(item[key]);
      seen.set(k, (seen.get(k) ?? 0) + 1);
    }
    const problems = new Map<Item, string>();
    for (const item of value) {
      const k = keyString(item[key]);
      if (k === "") problems.set(item, t("mappingConfig.collection.keyRequired", { key }));
      else if ((seen.get(k) ?? 0) > 1) problems.set(item, t("mappingConfig.collection.keyDuplicate", { key, value: k }));
    }
    return problems;
  }, [value, key, t]);

  const invalidMessage = keyProblems.size > 0 ? [...keyProblems.values()][0] : null;
  useEffect(() => {
    onInvalid?.(invalidMessage);
  }, [invalidMessage, onInvalid]);

  const statusOf = (item: Item): "added" | "changed" | null => {
    const before = baselineByKey.get(keyString(item[key]));
    if (!before) return "added";
    return deepEqual(before, item) ? null : "changed";
  };

  const removed = useMemo(() => {
    const present = new Set(value.map((item) => keyString(item[key])));
    return baseline.filter((item) => !present.has(keyString(item[key])));
  }, [baseline, value, key]);

  const emit = (items: Item[]) => onChange?.(items, collectionOps(baseline, items, key));

  const updateItem = (target: Item, field: string, next: unknown) => {
    const updated = { ...target, [field]: next };
    ids.current.set(updated, rowId(target));
    emit(value.map((item) => (item === target ? updated : item)));
  };

  const removeItem = (target: Item) => {
    if (editing === rowId(target)) setEditing(null);
    emit(value.filter((item) => item !== target));
  };

  const addItem = () => {
    const item: Item = {};
    for (const field of itemFields) {
      const empty = emptyValueFor(field);
      if (empty !== undefined) item[field.name] = empty;
    }
    setEditing(rowId(item));
    setQuery("");
    setFilter("all");
    setPage(1);
    emit([item, ...value]);
  };

  const restore = (item: Item) => emit([...value, item]);

  const needle = query.trim().toLowerCase();
  const visible = value.filter((item) => {
    if (filter === "changed" && statusOf(item) === null) return false;
    if (filter === "errors" && !rowErrors[keyString(item[key])] && !keyProblems.has(item)) return false;
    if (!needle) return true;
    return itemFields.some((field) => formatValue(item[field.name]).toLowerCase().includes(needle));
  });
  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const isEditable = (item: Item, field: FieldDescriptor) =>
    !readOnly && editing === rowId(item) && field.owner !== "code" && (field.name !== key || !baselineByKey.has(keyString(item[key])));

  const columns: Array<DataTableColumn<Item>> = [
    col.badge<Item>("status", "", (item) => {
      const status = statusOf(item);
      const errors = [...(rowErrors[keyString(item[key])] ?? []), ...(keyProblems.has(item) ? [keyProblems.get(item)!] : [])];
      return (
        <Group gap={spacePx[1]} wrap="nowrap">
          {status && (
            <Badge size="xs" variant="light" color={status === "added" ? "green" : "blue"}>
              {t(`mappingConfig.collection.status.${status}`)}
            </Badge>
          )}
          {errors.length > 0 && (
            <Tooltip label={errors.join("\n")} multiline maw={360}>
              <Badge size="xs" color="red" data-testid="row-error">{t("mappingConfig.collection.error")}</Badge>
            </Tooltip>
          )}
        </Group>
      );
    }, 104),
    ...itemFields.map((field): DataTableColumn<Item> => ({
      key: field.name,
      header: field.label,
      width: field.name === key ? 112 : field.kind === "boolean" ? COLUMN_WIDTH.number : undefined,
      render: (item) =>
        isEditable(item, field) ? (
          <ValueInput
            descriptor={field}
            value={item[field.name]}
            onChange={(next) => updateItem(item, field.name, next)}
            compact
            label={undefined}
          />
        ) : (
          <Text size="sm" truncate title={formatValue(item[field.name])} ff={field.name === key ? "monospace" : undefined}>
            {formatValue(item[field.name]) || "-"}
          </Text>
        ),
    })),
    ...(readOnly
      ? []
      : [col.actions<Item>("", (item) => [
          editing === rowId(item)
            ? { label: t("mappingConfig.collection.done"), icon: <IconCheck size={16} />, onClick: () => setEditing(null) }
            : { label: t("mappingConfig.collection.editRow"), icon: <IconEdit size={16} />, onClick: () => setEditing(rowId(item)) },
          { label: t("mappingConfig.collection.removeRow"), icon: <IconTrash size={16} />, color: "red", onClick: () => removeItem(item) },
        ])]),
  ];

  return (
    <Stack gap="sm" data-collection={descriptor.path}>
      <Group justify="space-between" gap="sm">
        <Group gap="sm">
          <TextInput
            size="xs"
            leftSection={<IconSearch size={14} />}
            placeholder={t("mappingConfig.collection.search")}
            aria-label={t("mappingConfig.collection.search")}
            value={query}
            onChange={(e) => {
              setQuery(e.currentTarget.value);
              setPage(1);
            }}
            w={240}
          />
          <SegmentedControl
            size="xs"
            value={filter}
            onChange={(next) => {
              setFilter(next as Filter);
              setPage(1);
            }}
            data={[
              { value: "all", label: t("mappingConfig.collection.filter.all") },
              { value: "changed", label: t("mappingConfig.collection.filter.changed") },
              { value: "errors", label: t("mappingConfig.collection.filter.errors") },
            ]}
          />
        </Group>
        <Group gap="sm">
          <Text size="xs" c="dimmed">
            {t("mappingConfig.collection.count", { count: value.length, shown: visible.length })}
          </Text>
          {!readOnly && (
            <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addItem}>
              {t("mappingConfig.collection.addRow")}
            </Button>
          )}
        </Group>
      </Group>
      {keyField === undefined && (
        <Text size="xs" c="orange">{t("mappingConfig.collection.keyMissing", { key })}</Text>
      )}
      <AdminTable
        columns={columns}
        data={pageRows}
        getRowKey={rowId}
        emptyMessage={t("mappingConfig.collection.empty")}
        pagination={visible.length > pageSize ? { page: currentPage, total: visible.length, pageSize, onChange: setPage } : undefined}
      />
      {removed.length > 0 && (
        <Stack gap={spacePx[1]}>
          <Text size="xs" c="dimmed">{t("mappingConfig.collection.removed", { count: removed.length })}</Text>
          <Group gap="xs">
            {removed.map((item) => (
              <Badge
                key={keyString(item[key])}
                color="red"
                variant="light"
                rightSection={readOnly ? undefined : (
                  <ActionIcon size="xs" variant="transparent" color="red" aria-label={t("mappingConfig.collection.restore")}
                              onClick={() => restore(item)}>
                    <IconArrowBackUp size={12} />
                  </ActionIcon>
                )}
              >
                {keyString(item[key])}
              </Badge>
            ))}
          </Group>
        </Stack>
      )}
    </Stack>
  );
};
